import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { projectApi, taskApi, memberApi } from '@/services/api'
import type { Task } from '@/types'

const QUERY_KEYS = {
  projects: () => ['projects'],
  project: (id: string) => ['projects', id],
  projectProgress: (id: string) => ['projects', id, 'progress'],
  projectTasks: (id: string, filters?: Record<string, string>) => [
    'projects',
    id,
    'tasks',
    filters,
  ],
  projectMembers: (id: string) => ['projects', id, 'members'],
}

export function useProjects() {
  return useQuery({
    queryKey: QUERY_KEYS.projects(),
    queryFn: async () => {
      const response = await projectApi.getProjects()
      return response.data.projects
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  })
}

export function useProject(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.project(id),
    queryFn: async () => {
      const response = await projectApi.getProject(id)
      return response.data
    },
    staleTime: 30 * 1000,
    enabled: !!id,
  })
}

export function useProjectProgress(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.projectProgress(id),
    queryFn: async () => {
      const response = await projectApi.getProgress(id)
      return response.data
    },
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    enabled: !!id,
  })
}

export function useProjectTasks(id: string, filters?: Record<string, string>) {
  return useQuery({
    queryKey: QUERY_KEYS.projectTasks(id, filters),
    queryFn: async () => {
      const response = await taskApi.getTasks(id, filters)
      return response.data.tasks
    },
    staleTime: 30 * 1000,
    enabled: !!id,
  })
}

export function useProjectMembers(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.projectMembers(id),
    queryFn: async () => {
      const response = await memberApi.getMembers(id)
      return response.data.members
    },
    staleTime: 30 * 1000,
    enabled: !!id,
  })
}

export function useUpdateTask(projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ taskId, data }: { taskId: string; data: Partial<Task> }) => {
      const response = await taskApi.updateTask(projectId, taskId, data)
      return response.data
    },
    onMutate: async ({ taskId, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.projectTasks(projectId),
      })

      // Snapshot previous data
      const previousTasks = queryClient.getQueryData<Task[]>(
        QUERY_KEYS.projectTasks(projectId),
      )

      if (previousTasks) {
        // Update cache optimistically
        queryClient.setQueryData(
          QUERY_KEYS.projectTasks(projectId),
          previousTasks.map((task) =>
            task.id === taskId ? { ...task, ...data } : task,
          ),
        )
      }

      return { previousTasks }
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousTasks) {
        queryClient.setQueryData(
          QUERY_KEYS.projectTasks(projectId),
          context.previousTasks,
        )
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.projectTasks(projectId),
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.projectProgress(projectId),
      })
    },
  })
}

export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      name: string
      description?: string
      acquiredCompanyName: string
      templateId?: string
    }) => {
      const response = await projectApi.createProject(data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.projects(),
      })
    },
  })
}

export function useAddMember(projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      entraObjectId: string
      entraType: string
      role: string
      sendNotification?: boolean
    }) => {
      const response = await memberApi.addMember(projectId, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.projectMembers(projectId),
      })
    },
  })
}

export function useRemoveMember(projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (memberId: string) => {
      await memberApi.removeMember(projectId, memberId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.projectMembers(projectId),
      })
    },
  })
}

export function useAssignTask(projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      taskId,
      data,
    }: {
      taskId: string
      data: { entraObjectId: string; entraType: string; sendNotification?: boolean }
    }) => {
      await taskApi.assignTask(projectId, taskId, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.projectTasks(projectId),
      })
    },
  })
}
